/**
 * voice-v8 — Werkzeugkarten: Mac: Apps (Numbers, Notizen, Musik …), Bildschirmsteuerung und Abläufe.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { baueKarte, fehlerBlock, kartenTitel } from "../karte.js";
import { menschlicherFehler } from "../fehler-worte.js";
import { werkzeugName } from "../werkzeug-namen.js";
import { trimText, trimUrl, unwrapResult } from "./hilfe.js";

// --- Mac-App Cards (Pre.249) -------------------------------------------

export function buildMacAppCard(tool: string, result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte(tool.replace(/_/g, "-"));
  const labels: Record<string, string> = {
    mac_numbers_table: "Numbers · Tabelle",
    mac_pages_doc: "Pages · Dokument",
    mac_keynote_present: "Keynote · Präsentation",
    mac_notes_create: "Notes · neue Notiz",
    mac_notes_append: "Notes · ergänzt",
    mac_notes_search: "Notes · Suche",
    mac_reminders_create: "Reminder · neu",
    mac_reminders_list: "Reminders · Liste",
    mac_mail_compose: "Mail · Compose",
    mac_music: "Music",
    mac_safari_open: "Safari · öffnet",
    mac_finder_reveal: "Finder · zeigt",
    mac_spotlight_search: "Spotlight",
    mac_clipboard: "Clipboard",
    mac_system_volume: "Lautstärke",
    mac_applescript: "AppleScript",
  };
  el.appendChild(kartenTitel(labels[tool] ?? tool));

  if (r.ok === false || (typeof r.error === "string" && r.error)) {
    el.appendChild(
      fehlerBlock(
        menschlicherFehler(
          r,
          "Das hat gerade nicht geklappt.",
        ),
      ),
    );
    return el;
  }

  const body = document.createElement("div");
  body.className = "tool-browser-detail";
  const out = typeof r.output === "string" ? r.output : "";
  body.textContent = out ? trimText(out, 300) : "OK";
  el.appendChild(body);
  return el;
}

// --- Screen-Agent / PawBot Desktop Cards (Pre.236) ----------------------

/** Das Fenster eines Bildschirmfotos (Tickets 3020/2806), falls eines mitkam. */
function fensterVon(x: unknown): { app?: unknown; titel?: unknown } | null {
  return x !== null && typeof x === "object" ? x : null;
}

export function buildScreenCard(tool: string, result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte(tool.replace(/_/g, "-"));
  const labels: Record<string, string> = {
    screen_context: "Mac · aktive App",
    screen_take_screenshot: "Mac · Screenshot",
    screen_look: "Mac · Blick",
    screen_mouse_click: "Mac · Klick",
    screen_mouse_move: "Mac · Maus bewegt",
    screen_double_click: "Mac · Doppelklick",
    screen_right_click: "Mac · Rechtsklick",
    screen_type_text: "Mac · Tastatur",
    screen_hotkey: "Mac · Hotkey",
    screen_scroll: "Mac · Scroll",
    screen_list_apps: "Mac · Apps",
    claude_task: "Mac · Shell",
  };
  el.appendChild(kartenTitel(labels[tool] ?? tool));

  if (r.ok === false || (typeof r.error === "string" && r.error)) {
    el.appendChild(
      fehlerBlock(
        menschlicherFehler(
          r,
          "Das hat gerade nicht geklappt.",
        ),
      ),
    );
    return el;
  }

  const body = document.createElement("div");
  body.className = "tool-browser-detail";
  let detail = "";
  switch (tool) {
    case "screen_context": {
      const app = typeof r.app === "string" ? r.app : "?";
      const url = typeof r.url === "string" && r.url ? trimUrl(r.url, 50) : "";
      const title =
        typeof r.title === "string" && r.title ? trimText(r.title, 50) : "";
      detail = [app, title, url].filter(Boolean).join(" · ");
      break;
    }
    case "screen_take_screenshot":
    case "screen_look": {
      const data = typeof r.data === "string" ? r.data : "";
      const sz = r.screen_size as [number, number] | null | undefined;
      const szStr = Array.isArray(sz) ? `${sz[0]}×${sz[1]}` : "";
      const dataStr = data ? `${Math.round(data.length / 1024)} KB JPG` : "";
      // 06.10.2026 (Tickets 3020/2806): ein einzelnes Fenster — welches; die alte App nahm stattdessen alles auf.
      // ⚠️ 14.10.2026: kein `as` hier — das Tor (eslint --fix) hielt die Zusicherung für
      //    überflüssig und strich sie, danach fiel tsc. Eine Funktion kann es nicht streichen.
      const f = fensterVon(r.fenster);
      const fensterStr = f && typeof f.app === "string"
        ? [f.app, typeof f.titel === "string" ? trimText(f.titel, 40) : ""].filter(Boolean).join(" · ")
        : r.ausschnitt === "ganzer-bildschirm" ? "ganzer Bildschirm statt Fenster" : "";
      detail = [fensterStr, szStr, dataStr].filter(Boolean).join(" · ");
      break;
    }
    case "screen_mouse_click":
    case "screen_mouse_move":
    case "screen_double_click":
    case "screen_right_click":
      detail = `(${r.x ?? "?"}, ${r.y ?? "?"}) · ${r.ok === true ? "OK" : "fail"}`;
      break;
    case "screen_type_text":
      detail = `„${trimText(String(r.text ?? ""), 60)}"`;
      break;
    case "screen_hotkey":
      detail = String(r.keys ?? "");
      break;
    case "screen_scroll":
      detail = `${String(r.direction ?? "")} · ${r.amount ?? ""}`;
      break;
    case "screen_list_apps": {
      const apps = (r.apps as string[] | undefined) ?? [];
      detail = `${apps.length} Apps · ${apps.slice(0, 5).join(", ")}${apps.length > 5 ? "…" : ""}`;
      break;
    }
    case "claude_task": {
      const success = r.success === true;
      const out = typeof r.output === "string" ? r.output : "";
      const preview = out.split("\n").slice(0, 4).join("\n");
      detail = `${success ? "OK" : "FAIL"}\n${trimText(preview, 300)}`;
      break;
    }
  }
  body.textContent = detail || "OK";
  el.appendChild(body);
  return el;
}

/**
 * Mehrere Schritte in einem Aufruf. (30.09.2026, Ticket 2830) Eine Zeile mit
 * Zahl und Dauer; bei einem Abbruch der Satz für Menschen (`satz`) — nie die
 * Ergebnisse der Schritte (die tragen Seitentext) und nie `hinweis`: der ist
 * für das Modell geschrieben („Entscheide von hier aus neu“, „siehe
 * ergebnisse“) und trägt Schlüsselwörter (Prüfbefund 30.09.2026).
 */
export function buildAblaufCard(tool: string, result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte(tool.replace(/_/g, "-"));
  el.appendChild(kartenTitel(werkzeugName(tool)));
  if (r.ok === false || (typeof r.error === "string" && r.error)) {
    const satz = typeof r.satz === "string" && r.satz.trim().length > 3 ? r.satz.trim() : "Die Schritte sind nicht bis zum Ende gelaufen.";
    el.appendChild(fehlerBlock(satz));
    return el;
  }
  const body = document.createElement("div");
  body.className = "tool-browser-detail";
  const n = typeof r.ausgefuehrt === "number" ? r.ausgefuehrt : 0;
  const sekunden = typeof r.dauer_ms === "number" ? ` · ${(r.dauer_ms / 1000).toFixed(1).replace(".", ",")} s` : "";
  body.textContent = `${n} ${n === 1 ? "Schritt" : "Schritte"} ausgeführt${sekunden}`;
  el.appendChild(body);
  return el;
}
